import { useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import type {
  AirBooking,
  AirPassengerBreakdown,
  Amenity,
  FlightSegment,
  SelectableFare,
  SelectedSpecialService,
} from '../../../api/types/air';
import type { ShoppingCart } from '../../../api/types/shoppingCart';
import { AirlineLogo, airlineCodeOf, airlineNameOf } from '../../../components/AirlineLogo';

const RELEVANT_STATUSES = new Set([
  'Booked',
  'Cancelled',
  'Prebooked',
  'StagedBooking',
  'Allocated',
  'PassengersUpdated',
]);

/** Fiyat kuralları paneli sadece bu sağlayıcılar için — legacy görünümdeki aynı liste. */
const FARE_RULES_PROVIDERS = new Set([
  'TurkishAirlines',
  'Arkman',
  'BiletBank',
  'Ajet',
  'Pegasus',
  'THYNDC',
  'Turna',
  'Paximum',
  'Travelport',
  'Corendon',
]);

const PAX_TYPE_LABELS: Record<string, string> = {
  ADT: 'Yetişkin',
  CHD: 'Çocuk',
  CNN: 'Çocuk',
  INF: 'Bebek',
  STD: 'Öğrenci',
  SRC: '+65 Yaş',
  MIL: 'Asker',
  YTH: 'Genç',
};

function formatFlightDate(dateTime?: string): string {
  if (!dateTime) return '—';
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return dateTime;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatFlightTime(dateTime?: string): string {
  if (!dateTime) return '—';
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return dateTime;
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function formatMoney(amount?: number): string {
  return (amount ?? 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function timeUnitLabel(unit?: string): string {
  if (!unit) return '';
  const map: Record<string, string> = {
    HOUR: 'saat',
    HOURS: 'saat',
    DAY: 'gün',
    DAYS: 'gün',
    MINUTE: 'dakika',
    MINUTES: 'dakika',
    WEEK: 'hafta',
    WEEKS: 'hafta',
    MONTH: 'ay',
    MONTHS: 'ay',
  };
  return map[unit.toUpperCase()] ?? unit;
}

function timeConditionLabel(condition?: string): string {
  if (!condition) return '';
  const map: Record<string, string> = {
    LESS: 'az',
    MORE: 'fazla',
    BEFORE: 'önce',
    AFTER: 'sonra',
  };
  return map[condition.toUpperCase()] ?? condition;
}

/** Bir bacağın kalkış → varış şehir etiketi (multi-city başlıkları için); şehir adı
 *  yoksa havalimanı koduna düşer. */
function legCityLabel(segments: FlightSegment[]): string {
  const first = segments[0];
  const last = segments[segments.length - 1];
  const dep = first?.departureCityName || first?.departureAirportCode || '';
  const arr = last?.arrivalCityName || last?.arrivalAirportCode || '';
  return [dep, arr].filter(Boolean).join(' → ');
}

type LegGroup = {
  key: string;
  /** Kart içi yön rozeti: "Gidiş" | "Dönüş" | "1. Uçuş" */
  shortLabel: string;
  /** Bacak başlığı: kısa etiket + varsa "İstanbul → Ankara" */
  label: string;
  segments: FlightSegment[];
  /** Multi-city modunda dolu — bacak = tek AirBooking (kendi PNR / durum / sağlayıcısı). */
  booking?: AirBooking;
  /** Bu bacağın (AirBooking'in) bilet numaraları — multi-city başlığında gösterilir. */
  ticketNumbers: string[];
};

/**
 * Sepetteki uçuş segmentlerini görüntülenecek bacaklara böler:
 *  - **Çok noktalı (multi-city):** `airBookings.length > 1` ve tüm segmentler gidiş yönü
 *    (`directionInd` 0 ya da yok) — legacy `FlightController` koşulu
 *    `AirBookings.Count > 1 && AirBookings.All(b => b.FlightSegments.All(s => s.DirectionInd == 0))`.
 *    Her `AirBooking` bir bacak; "N. Uçuş: Şehir → Şehir" başlığıyla, kendi PNR/durum rozetleriyle.
 *  - **Gidiş-dönüş / tek yön:** `directionInd` ile Gidiş (0) / Dönüş (1) ayrımı. `directionInd`
 *    hiç yoksa tek "Gidiş" grubu. 1'den büyük değerler ("N. Uçuş") sona eklenir — hiçbir
 *    segment sessizce düşmez (eski davranışın multi-city hatası buydu).
 */
export function buildLegGroups(airBookings: AirBooking[]): LegGroup[] {
  const bookingsWithSegments = airBookings.filter((b) => (b.flightSegments?.length ?? 0) > 0);
  const allSegments = bookingsWithSegments.flatMap((b) => b.flightSegments ?? []);
  if (allSegments.length === 0) return [];

  const isMultiCity =
    bookingsWithSegments.length > 1 && allSegments.every((s) => (s.directionInd ?? 0) === 0);

  if (isMultiCity) {
    return bookingsWithSegments.map((b, i) => {
      const segments = (b.flightSegments ?? [])
        .slice()
        .sort((x, y) => (x.sequenceNo ?? 0) - (y.sequenceNo ?? 0));
      const cities = legCityLabel(segments);
      const ticketNumbers = Array.from(
        new Set((b.passengerBreakdowns ?? []).map((pb) => pb.ticketNumber).filter((t): t is string => !!t))
      );
      return {
        key: b.id ?? `leg-${i}`,
        shortLabel: `${i + 1}. Uçuş`,
        label: cities ? `${i + 1}. Uçuş: ${cities}` : `${i + 1}. Uçuş`,
        segments,
        booking: b,
        ticketNumbers,
      };
    });
  }

  const hasDirectionInd = allSegments.some((s) => s.directionInd != null);
  if (!hasDirectionInd) {
    return [{ key: 'go', shortLabel: 'Gidiş', label: 'Gidiş', segments: allSegments, ticketNumbers: [] }];
  }

  const groups: LegGroup[] = [];
  const dep = allSegments.filter((s) => (s.directionInd ?? 0) === 0);
  const ret = allSegments.filter((s) => s.directionInd === 1);
  if (dep.length) groups.push({ key: 'go', shortLabel: 'Gidiş', label: 'Gidiş', segments: dep, ticketNumbers: [] });
  if (ret.length) groups.push({ key: 'return', shortLabel: 'Dönüş', label: 'Dönüş', segments: ret, ticketNumbers: [] });

  const extraInds = Array.from(
    new Set(allSegments.map((s) => s.directionInd).filter((d): d is number => d != null && d > 1))
  ).sort((a, b) => a - b);
  extraInds.forEach((ind) => {
    groups.push({
      key: `d${ind}`,
      shortLabel: `${ind + 1}. Uçuş`,
      label: `${ind + 1}. Uçuş`,
      segments: allSegments.filter((s) => s.directionInd === ind),
      ticketNumbers: [],
    });
  });

  return groups;
}

function FlightSegmentCard({ segment, directionLabel }: { segment: FlightSegment; directionLabel: string }) {
  const airlineCode = airlineCodeOf(segment) ?? '';
  const flightNo = segment.flightNumber?.slice(2) ?? '';

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <Chip size="small" color="primary" label={directionLabel} />
        <Chip size="small" label={formatFlightDate(segment.departureDateTime)} />
        <Chip
          size="small"
          variant="outlined"
          label={[segment.departureCityName, segment.arrivalCityName].filter(Boolean).join(' - ')}
        />
      </Stack>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <AirlineLogo size={28} code={airlineCode} name={airlineNameOf(segment)} />
            <Box sx={{ minWidth: 70 }}>
              <Typography variant="subtitle2" title="Havayolu kodu">
                {airlineCode}
              </Typography>
              <Typography variant="caption" color="text.secondary" title="Uçuş numarası">
                {flightNo}
              </Typography>
            </Box>
            <Box sx={{ minWidth: 140 }}>
              <Typography variant="subtitle2" title="Kalkış saati">
                {formatFlightTime(segment.departureDateTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary" title="Kalkış havalimanı">
                {segment.departureAirportName} ({segment.departureAirportCode})
              </Typography>
            </Box>
            <Box sx={{ minWidth: 140 }}>
              <Typography variant="subtitle2" title="Varış saati">
                {formatFlightTime(segment.arrivalDateTime)}
              </Typography>
              <Typography variant="caption" color="text.secondary" title="Varış havalimanı">
                {segment.arrivalAirportName} ({segment.arrivalAirportCode})
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={segment.markettingAirlineName || segment.markettingAirline || '—'} />
            <Chip size="small" label={[segment.cabinType, segment.bookingClass].filter(Boolean).join(' - ')} />
            {segment.co2Emissions?.weight != null && (
              <Chip size="small" label={`Co2 Emissions - ${segment.co2Emissions.weight} ${segment.co2Emissions.weightUnit ?? ''}`} />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function FareRulesPanel({ fare, segments }: { fare: SelectableFare; segments: FlightSegment[] }) {
  const rows: React.ReactNode[] = [];

  if (fare.checkedBaggageAllowance) {
    if (fare.fareName === 'LIGHT') {
      rows.push(
        <TableRow key="light-bag">
          <TableCell>
            1 Adet Koltuk Altına Sığacak Çanta{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              (40x30x15 cm, 3 Kg)
            </Typography>
          </TableCell>
        </TableRow>
      );
    } else {
      rows.push(
        <TableRow key="checked-bag">
          <TableCell>
            {fare.checkedBaggageAllowance.kilos} kg bagaj hakkı ({fare.checkedBaggageAllowance.pieces} parça)
          </TableCell>
        </TableRow>
      );
      const carryOn = fare.carryOnBaggageAllowance;
      if (carryOn && carryOn.kilos) {
        rows.push(
          <TableRow key="carry-on">
            <TableCell>
              {carryOn.kilosString
                ? `${carryOn.pieces} Adet Kabin Bagajı (${carryOn.kilosString} Boyutta)`
                : `${carryOn.kilos} kg kabin bagaj hakkı (${carryOn.pieces} parça)`}
            </TableCell>
          </TableRow>
        );
      } else if (carryOn) {
        rows.push(
          <TableRow key="carry-on-pieces">
            <TableCell>{carryOn.pieces} parça kabin bagaj hakkı</TableCell>
          </TableRow>
        );
      }
    }
  }

  (fare.serviceMessages ?? []).forEach((msg, i) => {
    (msg.serviceMessagesFlight ?? []).forEach((line, j) => {
      const seg = segments[i];
      rows.push(
        <TableRow key={`svc-${i}-${j}`}>
          <TableCell>
            {line}{' '}
            {seg && (
              <Typography component="span" variant="caption" color="error">
                ({seg.departureAirportCode}-{seg.arrivalAirportCode})
              </Typography>
            )}
          </TableCell>
        </TableRow>
      );
    });
  });

  if (fare.fareName === 'AVANTAJ' || fare.fareName === 'SAVER PLUS') {
    rows.push(
      <TableRow key="std-seat">
        <TableCell>Standart Koltuk Seçimi</TableCell>
      </TableRow>,
      <TableRow key="sandwich">
        <TableCell>Sandviç İkramı</TableCell>
      </TableRow>
    );
  }
  if (fare.fareCode === 'FLEX') {
    rows.push(
      <TableRow key="flex-seat">
        <TableCell>Standart Koltuk Seçimi</TableCell>
      </TableRow>
    );
  }
  if (fare.fareCode === 'PREMIUM') {
    rows.push(
      <TableRow key="premium-seat">
        <TableCell>Dilediğiniz Koltuk Seçimi</TableCell>
      </TableRow>
    );
  }
  if (fare.fareName === 'COMFORT FLEX') {
    rows.push(
      <TableRow key="comfort-seat">
        <TableCell>Dilediğiniz Koltuk Seçimi</TableCell>
      </TableRow>,
      <TableRow key="comfort-sandwich">
        <TableCell>Sandviç İkramı</TableCell>
      </TableRow>,
      <TableRow key="comfort-flex">
        <TableCell>Esnek İade/Değişiklik Hakkı</TableCell>
      </TableRow>
    );
  }

  (fare.penalties?.cancellationPenalties ?? []).forEach((c, i) => {
    let text: string;
    if (c.isRefundable) {
      text =
        c.penalty ??
        `Uçuş'a ${c.timeToDeparture?.timeAmount ?? ''} ${timeUnitLabel(c.timeToDeparture?.timeUnit)}ten ${timeConditionLabel(
          c.timeToDeparture?.timePeriodCondition
        )} zaman varsa ${c.percentageOrFixedAmount?.fixedAmount?.amount ?? ''} ${
          c.percentageOrFixedAmount?.fixedAmount?.currencyCode ?? ''
        } cezalı iptal`;
    } else {
      text = `Uçuş'a ${c.timeToDeparture?.timeAmount ?? ''} ${timeUnitLabel(c.timeToDeparture?.timeUnit)}ten ${timeConditionLabel(
        c.timeToDeparture?.timePeriodCondition
      )} zaman varsa iade edilemez`;
    }
    rows.push(
      <TableRow key={`cancel-${i}`}>
        <TableCell>{text}</TableCell>
      </TableRow>
    );
  });

  (fare.penalties?.changePenalties ?? []).forEach((c, i) => {
    let text: string;
    if (c.isChangeable) {
      text =
        c.penalty ??
        `Uçuş'a ${c.timeToDeparture?.timeAmount ?? ''} ${timeUnitLabel(c.timeToDeparture?.timeUnit)}ten ${timeConditionLabel(
          c.timeToDeparture?.timePeriodCondition
        )} zaman varsa ${c.percentageOrFixedAmount?.fixedAmount?.amount ?? ''} ${
          c.percentageOrFixedAmount?.fixedAmount?.currencyCode ?? ''
        } cezalı değişiklik`;
    } else {
      text = `Uçuş'a ${c.timeToDeparture?.timeAmount ?? ''} ${timeUnitLabel(c.timeToDeparture?.timeUnit)}ten ${timeConditionLabel(
        c.timeToDeparture?.timePeriodCondition
      )} zaman varsa değişiklik yapılamaz`;
    }
    rows.push(
      <TableRow key={`change-${i}`}>
        <TableCell>{text}</TableCell>
      </TableRow>
    );
  });

  return (
    <Paper variant="outlined" sx={{ flex: '1 1 320px' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          {fare.directionInd === 0 ? 'Gidiş: ' : fare.directionInd === 1 ? 'Dönüş: ' : ''}
          {fare.fareName} : {fare.fareCode} - {fare.cabin}
        </Typography>
      </Box>
      <Table size="small">
        <TableBody>{rows}</TableBody>
      </Table>
    </Paper>
  );
}

/**
 * Rezervasyon detayındaki "Uçak Bilgi Detayları" bloğu — legacy Razor görünümünün React
 * karşılığı: Gidiş/Dönüş uçuş kartları, yolcu bazlı ücret kırılımı tablosu (+ koltuk
 * numarası, atanmışsa), operasyon havayolu uyarıları, ücretsiz eklenen hizmetler,
 * ve sağlayıcıya göre bagaj/iptal/değişiklik kuralları paneli.
 *
 * Koltuk numarası: legacy `getSeatNumber` yardımcı fonksiyonunun kaynağı doğrulandı —
 * `shoppingCart.selectedAdditionalServices.selectedSpecialServices`'te `ssrCode==='SEAT'`
 * olan kayıtların `speqCode`'u gerçek koltuk numarasını taşıyor; yolcuya eşleme
 * `paxReference` (paxReferanceId) VEYA `passengerIndex` (Passengers dizisindeki 1'den
 * başlayan sıra no) eşleşmesiyle yapılıyor, ve bu sadece THYNDC/TurkishAirlines
 * rezervasyonlarında geçerli. `ShoppingCart` tipi `selectedAdditionalServices`'i henüz
 * modellemiyor (index signature üzerinden okunuyor) — veri yoksa hücre boş kalır.
 */
export function AirBookingFlightDetails({
  shoppingCart,
  isCorporate,
}: {
  shoppingCart: ShoppingCart;
  isCorporate: boolean;
}) {
  const airBookings = useMemo(() => (shoppingCart.airBookings ?? []) as AirBooking[], [shoppingCart.airBookings]);

  const legGroups = useMemo(() => buildLegGroups(airBookings), [airBookings]);
  const isMultiCity = legGroups.some((g) => g.booking);

  const passengersByRefId = useMemo(() => {
    const map = new Map<string, { name: string }>();
    (shoppingCart.passengers ?? []).forEach((p) => {
      if (!p.paxReferanceId) return;
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
      if (name) map.set(p.paxReferanceId, { name });
    });
    return map;
  }, [shoppingCart.passengers]);

  const seatsByPaxRefId = useMemo(() => {
    const map = new Map<string, string[]>();

    // Legacy `getSeatNumber`: sadece THYNDC/TurkishAirlines rezervasyonlarında koltuk
    // numarası ataması yapılıyor.
    const seatCapable = airBookings.some((b) => b.provider === 'THYNDC' || b.provider === 'TurkishAirlines');
    if (!seatCapable) return map;

    const selectedServices =
      (
        shoppingCart as unknown as {
          selectedAdditionalServices?: { selectedSpecialServices?: SelectedSpecialService[] };
        }
      ).selectedAdditionalServices?.selectedSpecialServices ?? [];
    if (!selectedServices.some((s) => s.ssrCode === 'SEAT')) return map;

    const passengers = shoppingCart.passengers ?? [];
    passengers.forEach((p, i) => {
      const refId = p.paxReferanceId;
      if (!refId) return;
      // Legacy: `Model.Passengers.FindIndex(x => x.PaxReferanceId == paxReferenceId) + 1`.
      const paxOrdinal = String(i + 1);
      const seatNumbers = selectedServices
        .filter((s) => s.ssrCode === 'SEAT' && (s.paxReference === refId || s.passengerIndex === paxOrdinal))
        .map((s) => s.speqCode)
        .filter((s): s is string => !!s);
      if (seatNumbers.length) map.set(refId, Array.from(new Set(seatNumbers)));
    });
    return map;
  }, [airBookings, shoppingCart]);

  const { breakdowns, totals, currency } = useMemo(() => {
    const relevant = airBookings.filter((b) => b.status && RELEVANT_STATUSES.has(b.status));
    const totalsAcc = {
      baseFare: 0,
      totalTax: 0,
      totalServiceFee: 0,
      totalLateInterest: 0,
      totalMarkupFare: 0,
      totalMarkupFareList: 0,
      totalFare: 0,
    };
    let curr = '';
    const map = new Map<string, AirPassengerBreakdown>();
    relevant.forEach((booking) => {
      curr = booking.currency ?? curr;
      totalsAcc.baseFare += booking.baseFare ?? 0;
      totalsAcc.totalTax += booking.totalTax ?? 0;
      totalsAcc.totalServiceFee += booking.totalServiceFee ?? 0;
      totalsAcc.totalLateInterest += booking.totalLateInterest ?? 0;
      totalsAcc.totalMarkupFare += booking.totalMarkupFare ?? 0;
      totalsAcc.totalMarkupFareList += booking.totalMarkupFareList ?? 0;
      totalsAcc.totalFare += booking.totalFare ?? 0;

      (booking.passengerBreakdowns ?? []).forEach((pb) => {
        const refId = pb.paxReference?.paxReferanceId;
        if (!refId) return;
        const existing = map.get(refId);
        if (existing) {
          existing.baseFare = (existing.baseFare ?? 0) + (pb.baseFare ?? 0);
          existing.totalFare = (existing.totalFare ?? 0) + (pb.totalFare ?? 0);
          existing.totalTax = (existing.totalTax ?? 0) + (pb.totalTax ?? 0);
          existing.totalMarkupFare = (existing.totalMarkupFare ?? 0) + (pb.totalMarkupFare ?? 0);
          existing.totalMarkupFareList = (existing.totalMarkupFareList ?? 0) + (pb.totalMarkupFareList ?? 0);
          existing.totalServiceFee = (existing.totalServiceFee ?? 0) + (pb.totalServiceFee ?? 0);
        } else {
          map.set(refId, { ...pb });
        }
      });
    });
    return { breakdowns: Array.from(map.values()), totals: totalsAcc, currency: curr };
  }, [airBookings]);

  const operatingMismatchWarnings: string[] = [];
  legGroups.forEach((leg) => {
    leg.segments.forEach((seg, i) => {
      if (seg.markettingAirline && seg.operatingAirline && seg.markettingAirline !== seg.operatingAirline) {
        const prefix = isMultiCity ? `${leg.shortLabel} — ` : '';
        operatingMismatchWarnings.push(
          `${prefix}${i + 1}. Uçuş'un Operasyonu'nu Yapan: ${seg.operatingAirlineName}`
        );
      }
    });
  });

  const relevantForNotes = airBookings.filter(
    (b) => b.status === 'Allocated' || b.status === 'PassengersUpdated' || b.status === 'Booked' || b.status === 'Prebooked'
  );
  const freeAmenities: Amenity[] = relevantForNotes.flatMap((b) => (b.amenities ?? []).filter((a) => a.isChargeable === false));
  const combinableOwc = relevantForNotes.some((b) => b.status === 'Booked' && b.isFromCombinableOwcList);
  const breakdownMessages: { paxCode?: string; message: string }[] = relevantForNotes.flatMap((b) =>
    (b.passengerBreakdowns ?? []).flatMap((pb) => (pb.messages ?? []).map((message) => ({ paxCode: pb.paxReference?.paxCode, message })))
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        {legGroups.map((leg) => (
          <Box key={leg.key} sx={{ mb: leg.booking ? 2.5 : 0 }}>
            {leg.booking && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {leg.label}
                </Typography>
                {leg.booking.bookingCode && (
                  <Chip size="small" color="primary" variant="outlined" label={`PNR: ${leg.booking.bookingCode}`} />
                )}
                {leg.ticketNumbers.map((t) => (
                  <Chip key={t} size="small" variant="outlined" label={`Bilet: ${t}`} />
                ))}
                {leg.booking.status && <Chip size="small" label={leg.booking.status} />}
                {leg.booking.provider && <Chip size="small" variant="outlined" label={leg.booking.provider} />}
              </Stack>
            )}
            {leg.segments.map((segment, i) => (
              <FlightSegmentCard
                key={segment.productId ?? `${leg.key}-${i}`}
                segment={segment}
                directionLabel={leg.shortLabel}
              />
            ))}
          </Box>
        ))}
      </Box>

      {breakdowns.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Yolcu</TableCell>
                <TableCell>Net</TableCell>
                <TableCell>Vergi</TableCell>
                <TableCell>Servis Bedeli</TableCell>
                {!isCorporate && (
                  <>
                    <TableCell>Vade</TableCell>
                    <TableCell>Acente Komisyonu</TableCell>
                    <TableCell>E.Acente Komisyonu</TableCell>
                  </>
                )}
                <TableCell>Koltuk</TableCell>
                <TableCell>Toplam</TableCell>
                <TableCell>Para birimi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {breakdowns.map((breakdown, i) => {
                const paxCode = breakdown.paxReference?.paxCode;
                const refId = breakdown.paxReference?.paxReferanceId;
                const taxTooltip =
                  breakdown.taxes && breakdown.taxes.length > 0
                    ? breakdown.taxes
                        .filter((t) => t.name !== 'EF')
                        .map((t) => `${t.name}: ${t.amount}`)
                        .join(' ')
                    : `VQ: ${breakdown.vq ?? 0} YR: ${breakdown.yr ?? 0}`;
                const seats = (refId && seatsByPaxRefId.get(refId)) || [];
                const passenger = refId ? passengersByRefId.get(refId) : undefined;
                return (
                  <TableRow key={refId ?? i}>
                    <TableCell>
                      <Typography variant="body2">
                        {passenger?.name || `${i + 1}. ${paxCode ? PAX_TYPE_LABELS[paxCode] ?? paxCode : 'Yolcu'}`}
                      </Typography>
                      {/* Multi-city'de her bacağın bileti farklı — birleşik satırda tek
                          numara yanıltıcı olur; bilet numaraları bacak başlıklarında. */}
                      {!isMultiCity && breakdown.ticketNumber && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {breakdown.ticketNumber}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatMoney(breakdown.baseFare)}</TableCell>
                    <TableCell>
                      <Tooltip title={`${taxTooltip} EF: ${breakdown.totalExtraFee ?? 0}`}>
                        <span>{formatMoney(breakdown.totalTax)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatMoney(breakdown.totalServiceFee)}</TableCell>
                    {!isCorporate && (
                      <>
                        <TableCell>{formatMoney(breakdown.totalLateInterest)}</TableCell>
                        <TableCell>{formatMoney(breakdown.totalMarkupFare)}</TableCell>
                        <TableCell>{formatMoney(breakdown.totalMarkupFareList)}</TableCell>
                      </>
                    )}
                    <TableCell>{seats.length > 0 ? seats.join(', ') : '—'}</TableCell>
                    <TableCell>{formatMoney(breakdown.totalFare)}</TableCell>
                    <TableCell>{currency}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Genel Toplam</TableCell>
                <TableCell>{formatMoney(totals.baseFare)}</TableCell>
                <TableCell>{formatMoney(totals.totalTax)}</TableCell>
                <TableCell>{formatMoney(totals.totalServiceFee)}</TableCell>
                {!isCorporate && (
                  <>
                    <TableCell>{formatMoney(totals.totalLateInterest)}</TableCell>
                    <TableCell>{formatMoney(totals.totalMarkupFare)}</TableCell>
                    <TableCell>{formatMoney(totals.totalMarkupFareList)}</TableCell>
                  </>
                )}
                <TableCell />
                <TableCell>{formatMoney(totals.totalFare)}</TableCell>
                <TableCell>{currency}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {operatingMismatchWarnings.map((msg, i) => (
        <Alert key={i} severity="info">
          {msg}
        </Alert>
      ))}

      {freeAmenities.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableBody>
              {freeAmenities.map((a, i) => (
                <TableRow key={i}>
                  <TableCell>{a.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {combinableOwc && (
        <Alert severity="warning">
          Farklı iki havayolu tarafından biletleme yapılmış olabilir. Detaylar için 'Not' kısmına bakınız.
        </Alert>
      )}

      {relevantForNotes.map((booking, bi) => {
        // Multi-city'de kurallar/notlar hangi bacağa ait — başlıkla belirt.
        const legLabel = isMultiCity
          ? legGroups.find((g) => g.booking?.id === booking.id)?.label
          : undefined;
        const showRules = booking.provider && FARE_RULES_PROVIDERS.has(booking.provider) && (booking.selectedFares?.length ?? 0) > 0;
        if (showRules) {
          const distinctFares = (booking.selectedFares ?? []).filter(
            (fare, idx, arr) => arr.findIndex((f) => f.directionInd === fare.directionInd) === idx
          );
          return (
            <Box key={booking.id ?? bi}>
              {legLabel && (
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {legLabel}
                </Typography>
              )}
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                {distinctFares.map((fare, fi) => (
                  <FareRulesPanel key={fi} fare={fare} segments={booking.flightSegments ?? []} />
                ))}
              </Stack>
            </Box>
          );
        }

        const isPromo = booking.fareInfoType === 'Promo';
        return (
          <Box key={booking.id ?? bi}>
            {legLabel && (
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {legLabel}
              </Typography>
            )}
            <Alert severity={isPromo ? 'error' : 'success'}>
              {booking.provider} {isPromo ? 'Promosyon' : 'Esnek'}
            </Alert>
            <Alert severity={isPromo ? 'warning' : 'info'} sx={{ mt: 1 }}>
              {isPromo
                ? 'Promosyon biletlerde iptal, iade ve değişiklik yapılamaz'
                : 'Esnek biletlerde iptal, iade ve değişiklik belirli kurallara tabiidir'}
            </Alert>
          </Box>
        );
      })}

      {breakdownMessages.map(({ paxCode, message }, i) => (
        <Alert key={i} severity="warning">
          {paxCode ? `${paxCode}: ` : ''}
          {message}
        </Alert>
      ))}
    </Box>
  );
}
